class WagmadeCartDrawer extends HTMLElement {
  connectedCallback() {
    if (this.dataset.cartReady === 'true') return;
    this.dataset.cartReady = 'true';

    this.panel = this.querySelector('.wagmade-cart-drawer__panel');
    this.body = this.querySelector('[data-cart-drawer-body]');
    this.support = this.querySelector('.wagmade-cart-drawer__support');
    this.closeButtons = Array.from(this.querySelectorAll('[data-cart-close]'));
    this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close()));
    
    // 使用全局事件委托，动态支持任何新渲染 DOM 中的 [data-cart-open] 按钮
    this.globalOpenHandler = async (event) => {
      const link = event.target.closest('[data-cart-open]');
      if (!link) return;
      event.preventDefault();
      try {
        await this.refreshFromSection('/cart');
      } catch (error) {
        // Keep the cart entry usable if section refresh is blocked.
      }
      this.open();
    };
    document.addEventListener('click', this.globalOpenHandler);

    this._escapeHandler = (event) => {
      if (event.key === 'Escape') this.close();
    };
    document.addEventListener('keydown', this._escapeHandler);
    document.addEventListener('wagmade:cart-open', () => this.open());

    this.addEventListener('click', this.handleClick.bind(this));
    this.addEventListener('change', this.handleChange.bind(this));
  }

  disconnectedCallback() {
    if (this.globalOpenHandler) {
      document.removeEventListener('click', this.globalOpenHandler);
    }
    if (this._escapeHandler) {
      document.removeEventListener('keydown', this._escapeHandler);
    }
  }

  async handleClick(event) {
    const addBtn = event.target.closest('.cross-sell-add-btn');
    if (addBtn) {
      event.preventDefault();
      if (this.classList.contains('is-loading')) return;

      const variantId = addBtn.getAttribute('data-variant-id');
      if (variantId) {
        this.classList.add('is-loading');
        try {
          const root = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
            },
            body: JSON.stringify({
              id: parseInt(variantId, 10),
              quantity: 1,
              sections: ['wagmade-cart-drawer'],
              sections_url: '/cart',
            }),
          });
          const data = await response.json();
          if (!response.ok) {
            throw new Error(data.description || 'Could not add to cart');
          }
          const drawerSection = data?.sections?.['wagmade-cart-drawer'];
          if (drawerSection) {
            this.renderSection(drawerSection);
          } else {
            await this.refreshFromSection('/cart');
          }
        } catch (error) {
          this.showErrorMessage(error.message || 'Please try again');
        } finally {
          this.classList.remove('is-loading');
        }
      }
      return;
    }

    const link = event.target.closest('a[href*="/cart/change"]');
    if (!link) return;

    event.preventDefault();
    if (this.classList.contains('is-loading')) return;

    const itemContainer = link.closest('[data-cart-line]');
    try {
      const url = new URL(link.href, window.location.origin);
      const line = url.searchParams.get('line');
      const id = url.searchParams.get('id');
      let quantity = url.searchParams.get('quantity');

      this.classList.add('is-loading');

      const payload = {};
      if (line) payload.line = parseInt(line, 10);
      else if (id) payload.id = id;

      // 移除操作的安全保护
      if (quantity === null || link.href.includes('remove') || link.textContent.trim().toLowerCase() === 'remove') {
        quantity = 0;
      }
      payload.quantity = parseInt(quantity, 10);

      // 如果是移除商品，延迟 300ms 让 CSS 高度与透明度动画折叠淡出
      if (payload.quantity === 0 && itemContainer) {
        itemContainer.classList.add('is-removing');
        setTimeout(async () => {
          try {
            await this.updateCartItem(payload);
          } catch (error) {
            itemContainer.classList.remove('is-removing');
            this.showErrorMessage(error.message || 'Please try again');
            this.classList.remove('is-loading');
          }
        }, 300);
      } else {
        await this.updateCartItem(payload);
      }
    } catch (error) {
      this.showErrorMessage(error.message || 'Please try again');
      this.classList.remove('is-loading');
    }
  }

  async handleChange(event) {
    const input = event.target;
    if (!input.matches('input[name="updates[]"]')) return;

    if (this.classList.contains('is-loading')) {
      input.value = input.defaultValue;
      return;
    }

    const itemContainer = input.closest('[data-cart-line]');
    if (!itemContainer) return;

    const line = itemContainer.getAttribute('data-cart-line');
    const quantity = parseInt(input.value, 10);

    if (isNaN(quantity) || quantity < 0) return;

    try {
      this.classList.add('is-loading');
      const payload = { line: parseInt(line, 10), quantity };

      if (quantity === 0) {
        itemContainer.classList.add('is-removing');
        setTimeout(async () => {
          try {
            await this.updateCartItem(payload);
          } catch (error) {
            itemContainer.classList.remove('is-removing');
            input.value = input.defaultValue;
            this.showErrorMessage(error.message || 'Please try again');
            this.classList.remove('is-loading');
          }
        }, 300);
      } else {
        await this.updateCartItem(payload);
      }
    } catch (error) {
      this.showErrorMessage(error.message || 'Please try again');
      input.value = input.defaultValue;
      this.classList.remove('is-loading');
    }
  }

  async updateCartItem(payload) {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart/change.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.description || 'Could not update cart');
    }

    await this.refreshFromSection('/cart');
    this.classList.remove('is-loading');
  }

  showErrorMessage(message) {
    let errorDiv = this.querySelector('.wagmade-cart-error-message');
    if (!errorDiv) {
      errorDiv = document.createElement('div');
      errorDiv.className = 'wagmade-cart-error-message';
      errorDiv.style.cssText = `
        background: #ffebe9;
        border: 1px solid #ffc8c4;
        color: #d12420;
        padding: 0.75rem 1rem;
        margin: 0.5rem 1rem;
        font-size: 0.88rem;
        text-align: center;
      `;
      this.panel.insertBefore(errorDiv, this.body);
    }
    errorDiv.textContent = message;
    errorDiv.style.display = 'block';

    setTimeout(() => {
      errorDiv.style.display = 'none';
    }, 4000);
  }

  open() {
    this._previousFocus = document.activeElement;
    this.setAttribute('open', '');
    this.panel?.setAttribute('aria-hidden', 'false');
    document.documentElement.setAttribute('data-cart-lock', '');
    const firstFocusable = this.panel?.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (firstFocusable) firstFocusable.focus();
  }

  close() {
    this.removeAttribute('open');
    this.panel?.setAttribute('aria-hidden', 'true');
    document.documentElement.removeAttribute('data-cart-lock');
    if (this._previousFocus && typeof this._previousFocus.focus === 'function') {
      this._previousFocus.focus();
      this._previousFocus = null;
    }
  }

  renderSection(sectionHtml) {
    const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const nextDrawer = parsed.querySelector('[data-cart-drawer]');
    const nextBody = parsed.querySelector('[data-cart-drawer-body]');
    const nextSupport = parsed.querySelector('.wagmade-cart-drawer__support');
    const nextCount = Number(nextDrawer?.dataset.cartCount || 0);

    if (!nextBody || !this.body) throw new Error('Cart drawer body missing');

    this.body.innerHTML = nextBody.innerHTML;

    if (nextSupport && this.support) {
      this.support.innerHTML = nextSupport.innerHTML;
    }

    this.dataset.cartCount = String(nextCount);
    this.updateHeaderCount(nextCount);
  }

  async refreshFromSection(pathname = '/') {
    const root = window.Shopify?.routes?.root || '/';
    const rootPrefix = root.endsWith('/') ? root.slice(0, -1) : root;
    const requestPath = pathname.startsWith('/') ? pathname : `/${pathname}`;
    const response = await fetch(`${rootPrefix}${requestPath}?section_id=wagmade-cart-drawer`, {
      cache: 'no-store',
      headers: { Accept: 'text/html' },
    });

    if (!response.ok) throw new Error('Cart drawer section refresh failed');

    const html = await response.text();
    if (!html.trim()) throw new Error('Cart drawer section empty');

    this.renderSection(html);
  }

  renderAddedItem(item) {
    if (!this.body || !item) throw new Error('Cart item missing');

    const currentCount = Number(this.dataset.cartCount || 0);
    const nextCount = currentCount + Number(item.quantity || 1);
    const title = this.escapeHtml(item.product_title || item.title || 'Added item');
    const variantTitle = item.variant_title && item.variant_title !== 'Default Title' ? this.escapeHtml(item.variant_title) : '';
    const image = this.escapeHtml(item.image || item.featured_image?.url || '');
    const imageAlt = this.escapeHtml(item.featured_image?.alt || title);
    const itemUrl = this.escapeHtml(item.url || '#');
    const quantity = Number(item.quantity || 1);
    const linePrice = this.formatMoney(item.final_line_price || item.line_price || item.price || 0);

    this.body.innerHTML = `
      <form action="/cart" method="post" class="wagmade-cart-drawer__form">
        <div class="wagmade-cart-drawer__items">
          <div class="wagmade-cart-drawer__item" data-cart-line="1">
            <a href="${itemUrl}" class="wagmade-cart-drawer__image">
              ${image ? `<img src="${image}" alt="${imageAlt}" loading="lazy">` : ''}
            </a>
            <div>
              <a href="${itemUrl}">${title}</a>
              ${variantTitle ? `<p>${variantTitle}</p>` : ''}
              <p>${linePrice}</p>
              <div class="wagmade-cart-drawer__qty">
                <a href="/cart/change?line=1&quantity=${quantity - 1}" aria-label="Decrease quantity">-</a>
                <input type="number" name="updates[]" value="${quantity}" min="0" aria-label="Quantity for ${title}">
                <a href="/cart/change?line=1&quantity=${quantity + 1}" aria-label="Increase quantity">+</a>
                <button type="submit" name="update" aria-label="Update quantity">Update</button>
              </div>
              <a href="/cart/change?line=1&quantity=0">Remove</a>
            </div>
          </div>
        </div>

        <div class="wagmade-cart-drawer__summary">
          <div>
            <span>Added to cart</span>
            <strong>${linePrice}</strong>
          </div>
          <p>Open your cart for the latest subtotal and quantity controls.</p>
          <button type="submit" name="checkout">Checkout</button>
          <a href="/collections/pet-totes">Continue Shopping</a>
        </div>
      </form>
    `;

    this.dataset.cartCount = String(nextCount);
    this.updateHeaderCount(nextCount);
  }

  updateHeaderCount(itemCount) {
    const cartLink = document.querySelector('.wagmade-header__cart');
    if (!cartLink) return;

    let count = cartLink.querySelector('sup');
    if (itemCount > 0) {
      if (!count) {
        count = document.createElement('sup');
        cartLink.append(count);
      }
      count.textContent = itemCount;
    } else {
      count?.remove();
    }
  }

  escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }

  formatMoney(cents) {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(cents || 0) / 100);
  }
}

class WagmadeCartPage extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.handleClick.bind(this));
    this.addEventListener('change', this.handleChange.bind(this));
  }

  async handleClick(event) {
    const link = event.target.closest('a[href*="/cart/change"]');
    if (!link) return;

    event.preventDefault();
    if (this.classList.contains('is-loading')) return;

    const itemContainer = link.closest('[data-cart-line]');
    try {
      const url = new URL(link.href, window.location.origin);
      const line = url.searchParams.get('line');
      const id = url.searchParams.get('id');
      let quantity = url.searchParams.get('quantity');

      this.classList.add('is-loading');

      const payload = {};
      if (line) payload.line = parseInt(line, 10);
      else if (id) payload.id = id;

      // 移除操作的安全保护
      if (quantity === null || link.href.includes('remove') || link.textContent.trim().toLowerCase() === 'remove') {
        quantity = 0;
      }
      payload.quantity = parseInt(quantity, 10);

      // 如果是移除商品，延迟 300ms 让 CSS 高度与透明度动画折叠淡出
      if (payload.quantity === 0 && itemContainer) {
        itemContainer.classList.add('is-removing');
        setTimeout(async () => {
          try {
            await this.updateCartItem(payload);
          } catch (error) {
            itemContainer.classList.remove('is-removing');
            this.showErrorMessage(error.message || 'Please try again');
            this.classList.remove('is-loading');
          }
        }, 300);
      } else {
        await this.updateCartItem(payload);
      }
    } catch (error) {
      this.showErrorMessage(error.message || 'Please try again');
      this.classList.remove('is-loading');
    }
  }

  async handleChange(event) {
    const input = event.target;
    if (!input.matches('input[name="updates[]"]')) return;

    if (this.classList.contains('is-loading')) {
      input.value = input.defaultValue;
      return;
    }

    const itemContainer = input.closest('[data-cart-line]');
    if (!itemContainer) return;
    const line = parseInt(itemContainer.getAttribute('data-cart-line'), 10);
    const quantity = parseInt(input.value, 10);

    if (isNaN(quantity) || quantity < 0) return;

    try {
      this.classList.add('is-loading');
      const payload = { line, quantity };

      if (quantity === 0) {
        itemContainer.classList.add('is-removing');
        setTimeout(async () => {
          try {
            await this.updateCartItem(payload);
          } catch (error) {
            itemContainer.classList.remove('is-removing');
            input.value = input.defaultValue;
            this.showErrorMessage(error.message || 'Please try again');
            this.classList.remove('is-loading');
          }
        }, 300);
      } else {
        await this.updateCartItem(payload);
      }
    } catch (error) {
      this.showErrorMessage(error.message || 'Please try again');
      input.value = input.defaultValue;
      this.classList.remove('is-loading');
    }
  }

  async updateCartItem(payload) {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart/change.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.description || 'Could not update cart');
    }

    await this.refreshFromSection();

    const drawer = document.querySelector('wagmade-cart-drawer');
    if (drawer) {
      drawer.dataset.cartCount = String(data.item_count);
      drawer.updateHeaderCount(data.item_count);
      await drawer.refreshFromSection('/cart');
    }

    this.classList.remove('is-loading');
  }

  async refreshFromSection() {
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', 'cart');

    const response = await fetch(url.toString(), {
      cache: 'no-store',
      headers: { Accept: 'text/html' },
    });

    if (!response.ok) throw new Error('Cart page refresh failed');

    const html = await response.text();
    if (!html.trim()) throw new Error('Cart page response empty');

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const nextContent = parsed.querySelector('wagmade-cart-page') || parsed.querySelector('.wagmade-cart');
    if (nextContent) {
      this.innerHTML = nextContent.innerHTML;
    }
  }

  showErrorMessage(message) {
    let errorDiv = this.querySelector('.wagmade-cart-error-message');
    if (!errorDiv) {
      errorDiv = document.createElement('div');
      errorDiv.className = 'wagmade-cart-error-message';
      errorDiv.style.cssText = `
        background: #ffebe9;
        border: 1px solid #ffc8c4;
        color: #d12420;
        padding: 0.75rem 1rem;
        margin-bottom: 1rem;
        font-size: 0.88rem;
        text-align: center;
        width: 100%;
      `;
      const container = this.querySelector('.wagmade-cart__layout') || this;
      container.insertBefore(errorDiv, container.firstChild);
    }
    errorDiv.textContent = message;
    errorDiv.style.display = 'block';

    setTimeout(() => {
      errorDiv.style.display = 'none';
    }, 4000);
  }
}

if (!customElements.get('wagmade-cart-drawer')) {
  customElements.define('wagmade-cart-drawer', WagmadeCartDrawer);
}

if (!customElements.get('wagmade-cart-page')) {
  customElements.define('wagmade-cart-page', WagmadeCartPage);
}
